import { useEffect, useState } from 'react';
import api, { fmtRp, errMsg, branchLabel, todayStr } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Wallet, Trash2, Plus, Loader2 } from 'lucide-react';

const CATEGORIES = ['operasional', 'gaji', 'kemasan', 'transport', 'lainnya'];

export default function Pengeluaran() {
  const { user, branches } = useAuth();
  const isAdmin = user.role === 'admin';
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ branch: isAdmin ? (branches[0]?.id || 'balaraja') : user.branch, category: 'operasional', amount: '', description: '', date: todayStr() });
  const [from, setFrom] = useState(todayStr().slice(0, 8) + '01');
  const [to, setTo] = useState(todayStr());
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get('/expenses', { params: { from_date: from, to_date: to } })
      .then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!(Number(form.amount) > 0)) return toast.error('Jumlah pengeluaran harus lebih dari 0');
    setSaving(true);
    try {
      await api.post('/expenses', { ...form, amount: Number(form.amount) });
      toast.success('Pengeluaran tercatat');
      setForm({ ...form, amount: '', description: '' });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Pengeluaran dihapus');
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const total = (list || []).reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="pengeluaran-page">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Pengeluaran</h2>
        <p className="text-sm text-muted-foreground">Catat semua pengeluaran toko dengan deskripsi</p>
      </div>

      <div className="lodi-card p-5" data-testid="expense-form">
        <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Wallet className="w-5 h-5 text-brand" /> Catat Pengeluaran</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {isAdmin && (
            <div>
              <span className="lodi-label">Cabang</span>
              <select value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} className="lodi-input" data-testid="expense-branch-select">
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <span className="lodi-label">Tanggal</span>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="lodi-input" data-testid="expense-date-input" />
          </div>
          <div>
            <span className="lodi-label">Kategori</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="lodi-input capitalize" data-testid="expense-category-select">
              {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
            </select>
          </div>
          <div>
            <span className="lodi-label">Jumlah (Rp) *</span>
            <input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="lodi-input" placeholder="0" data-testid="expense-amount-input" />
          </div>
          <div className="sm:col-span-2">
            <span className="lodi-label">Deskripsi</span>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="lodi-input" placeholder="cth: Beli plastik wrapping, bayar parkir supplier..." data-testid="expense-description-input" />
          </div>
        </div>
        <button onClick={submit} disabled={saving} data-testid="expense-submit-button"
          className="mt-4 px-6 py-3 rounded-xl bg-brand hover:bg-branddark disabled:opacity-40 text-white font-display font-bold flex items-center gap-2 transition-all active:scale-[0.98]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} SIMPAN
        </button>
      </div>

      <div className="lodi-card p-5" data-testid="expense-list">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-display font-bold">Daftar Pengeluaran</h3>
          <div className="flex items-center gap-2 text-sm">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="lodi-input w-auto" data-testid="expense-filter-from" />
            <span className="text-muted-foreground">—</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="lodi-input w-auto" data-testid="expense-filter-to" />
          </div>
        </div>
        {!list ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-brand animate-spin" /></div>
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground">Tidak ada pengeluaran pada periode ini.</p>
        ) : (
          <>
            <div className="space-y-2">
              {list.map((e) => (
                <div key={e.id} className="bg-elevated rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`expense-item-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{e.description || <span className="text-muted-foreground italic">Tanpa deskripsi</span>}</p>
                    <p className="text-xs text-muted-foreground">{e.date_wib} • <span className="capitalize">{e.category}</span> • {branchLabel(e.branch)} • oleh {e.recorded_by}</p>
                  </div>
                  <span className="font-bold text-amber-400 whitespace-nowrap">-{fmtRp(e.amount)}</span>
                  {isAdmin && (
                    <button onClick={() => remove(e.id)} data-testid={`delete-expense-${e.id}`}
                      className="w-8 h-8 rounded-lg bg-brand/10 border border-brand/25 text-brand flex items-center justify-center hover:bg-brand/20 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-line font-display font-bold">
              <span>Total Periode Ini</span>
              <span className="text-amber-400" data-testid="expense-total">{fmtRp(total)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
